/**
 * Auth — tenant-admin-dashboard login via Kong → auth-service
 * POST /api/v1/auth/tenant-dashboard/login (SECURITY.md §2.1 path family).
 */

import { kongBrowserFetchUrl, kongBrowserOrigin, KONG_LOGIN_404_HELP, KONG_OR_PROXY_502_HELP } from "@/lib/kongApi";
import { fetchWithTimeout, LOGIN_FETCH_TIMEOUT_MS } from "@/lib/fetchWithTimeout";
import { RefreshTransientError, RefreshInvalidError } from "@/lib/sessionExpiredEvent";

async function parseErrorMessage(res) {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  if (typeof data?.message === "string") return data.message;
  return res.statusText || "Request failed";
}

/**
 * @typedef {{
 *   status: "ok",
 *   token: string,
 *   email: string,
 *   tenant_id?: string,
 *   realm?: string,
 *   roles: string[],
 *   workspace_client_allowlist: string[],
 *   default_workspace_id?: string
 * }} LoginOkResult
 * @typedef {{ status: "totp_required", totp_session: string }} LoginTotpRequiredResult
 * @typedef {LoginOkResult | LoginTotpRequiredResult} LoginResult
 */

/**
 * @param {{ email: string, password: string, rememberMe: boolean }} params
 * @returns {Promise<LoginResult>}
 */
export async function loginWithPassword(params) {
  const email = String(params.email || "").trim();
  const password = String(params.password || "");
  if (!email || !password) {
    throw new Error("Please enter email and password.");
  }
  const url = kongBrowserFetchUrl("/api/v1/auth/tenant-dashboard/login");
  let res;
  try {
    res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      },
      LOGIN_FETCH_TIMEOUT_MS
    );
  } catch (e) {
    const origin = kongBrowserOrigin();
    const msg = e instanceof Error ? e.message : String(e);
    if (/timed out/i.test(msg)) {
      throw new Error(
        `${msg} If auth-service logs stay empty, traffic never reached it: check Kong auth-service upstream URL ` +
          `(e.g. kong-service/declarative/kong.yml must use the host+port where you run auth-service, default 8000), ` +
          `reload Kong after edits, and from the Kong host try: curl -sS -m 3 http://<auth-host>:<port>/health/live`
      );
    }
    const hint = origin
      ? `Could not reach Kong at ${origin}. Check network, TLS, and that Kong is listening.`
      : import.meta.env.DEV
        ? "Could not reach Kong via the Vite proxy. Set VITE_PROXY_API_TARGET to your Kong URL (e.g. http://127.0.0.1:8000) and restart `npm run dev`."
        : "Could not reach Kong. Set VITE_API_BASE_URL to your Kong origin.";
    throw new Error(`${hint} (${msg})`);
  }
  if (!res.ok) {
    const detail = await parseErrorMessage(res);
    if (res.status === 404) {
      const pathOnly =
        url.startsWith("http://") || url.startsWith("https://")
          ? new URL(url).pathname + new URL(url).search
          : url;
      throw new Error(
        `${KONG_LOGIN_404_HELP} Failed path: ${pathOnly}. Do not open this API URL in a browser tab; login is POST only from the /login page.`
      );
    }
    if (res.status === 502) {
      throw new Error(`${KONG_OR_PROXY_502_HELP} Raw detail: ${detail}`);
    }
    if (res.status === 503 || res.status === 504) {
      throw new Error(
        `Gateway returned ${res.status} (${res.statusText}). Kong or auth-service may be overloaded or timing out. Detail: ${detail}`
      );
    }
    throw new Error(detail);
  }
  const data = await res.json();
  if (String(data.status || "") === "totp_required" && data.totp_session) {
    return {
      status: "totp_required",
      totp_session: String(data.totp_session),
    };
  }
  const token = data.access_token || data.token;
  if (!token || typeof token !== "string") {
    throw new Error("Invalid login response: missing access token.");
  }
  const userEmail = data.user?.email || email;
  return {
    status: "ok",
    token,
    email: userEmail,
    tenant_id: data.tenant_id != null ? String(data.tenant_id) : "",
    realm: data.realm != null ? String(data.realm) : "",
    roles: Array.isArray(data.roles) ? data.roles.map(String) : [],
    workspace_client_allowlist: Array.isArray(data.workspace_client_allowlist)
      ? data.workspace_client_allowlist.map(String)
      : [],
    default_workspace_id:
      data.default_workspace_id != null ? String(data.default_workspace_id) : "",
  };
}

/**
 * @param {string | null | undefined} accessToken
 */
export async function logoutTenantDashboard(accessToken) {
  const tok = String(accessToken || "").trim();
  if (!tok) return;
  const url = kongBrowserFetchUrl("/api/v1/auth/tenant-dashboard/logout");
  try {
    await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: tok }),
      },
      15000
    );
  } catch {
    /* best-effort */
  }
}

/**
 * Refresh the tenant-admin-dashboard access token through auth-service.
 * @param {string | null | undefined} accessToken
 */
export async function refreshTenantDashboardToken(accessToken) {
  const tok = String(accessToken || "").trim();
  if (!tok) {
    // No token to refresh with — the session is genuinely gone.
    throw new RefreshInvalidError("Session expired. Please sign in again.");
  }
  const url = kongBrowserFetchUrl("/api/v1/auth/tenant-dashboard/refresh");
  const doPost = () =>
    fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: tok }),
      },
      15000
    );

  // A 429 means the server already has a refresh in flight for this token
  // (it holds a lock + caches the pending result). Retry a few times with a
  // short backoff to pick up that result instead of failing the session.
  const REFRESH_429_MAX_RETRIES = 3;
  let res;
  try {
    res = await doPost();
    for (let attempt = 1; res.status === 429 && attempt <= REFRESH_429_MAX_RETRIES; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
      res = await doPost();
    }
  } catch (e) {
    // Network error / timeout reaching the refresh endpoint — transient.
    throw new RefreshTransientError(e instanceof Error ? e.message : String(e));
  }

  if (!res.ok) {
    // 401 / invalid_grant = the refresh token itself is rejected → session over.
    // 429 (still, after retries) or 5xx = transient; do NOT kill the session.
    if (res.status === 401 || res.status === 400) {
      throw new RefreshInvalidError(await parseErrorMessage(res));
    }
    throw new RefreshTransientError(await parseErrorMessage(res));
  }
  const data = await res.json();
  const token = data.access_token || data.token;
  if (!token || typeof token !== "string") {
    throw new Error("Invalid refresh response: missing access token.");
  }
  return {
    token,
    email: data.user?.email || "",
    tenant_id: data.tenant_id != null ? String(data.tenant_id) : "",
    realm: data.realm != null ? String(data.realm) : "",
    roles: Array.isArray(data.roles) ? data.roles.map(String) : [],
    workspace_client_allowlist: Array.isArray(data.workspace_client_allowlist)
      ? data.workspace_client_allowlist.map(String)
      : [],
    default_workspace_id:
      data.default_workspace_id != null ? String(data.default_workspace_id) : "",
  };
}
